import type { ComponentProps, ReactNode } from 'react';

import { Radio } from '@playbook/atoms/inputs/radio';
import type { RadioSize } from '@playbook/atoms/inputs/radio';
import { cn } from '@playbook/utils';

import css from './styles.module.css';

const DefaultChoiceCardSize: RadioSize = 'md' as const;

type ChoiceCardEventHandlers = {
	[K in keyof ComponentProps<'input'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'input'>[K];
};

interface ChoiceCardDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface ChoiceCardProperties
	extends ChoiceCardEventHandlers, ChoiceCardDataAttributes {
	size?: RadioSize;
	disabled?: boolean;
	required?: boolean;

	/** Shared by every card in the set — it is what makes them one choice. */
	name: string;
	value: string;
	checked?: boolean;
	defaultChecked?: boolean;

	label: string;
	trailingLabel?: string;

	description?: ReactNode;
	trailingDescription?: ReactNode;
}

/**
 *	One option of a radio set, drawn as a card rather than a circle with text
 *	beside it. The control is `atoms/inputs/radio`; the card is a `<label>`
 *	wrapped around it, so the whole box is the hit area and the radio keeps
 *	every behaviour a native one has.
 *
 *	The card's two rows share a shape: a leading half that takes the space and
 *	a trailing half that keeps to its own width. Figma calls the top row's
 *	trailing half `price`; it holds a count or a flag as readily as money, so
 *	the prop is named for where it sits. The bottom row is Figma's
 *	`description-slot`, split into the two halves it draws so a caller cannot
 *	get the split wrong.
 */
export function ChoiceCard({
	size = DefaultChoiceCardSize,
	disabled = false,
	required = false,

	name,
	value,
	checked,
	defaultChecked,

	label,
	trailingLabel,

	description,
	trailingDescription,

	...props
}: ChoiceCardProperties): ReactNode {
	const className: string = cn(css.card, css[size]);

	return (
		<label data-size={size} className={className}>
			<span className={css.header}>
				<span className={css.label}>{label}</span>
				{trailingLabel && (
					<span className={css.trailingLabel}>{trailingLabel}</span>
				)}
				<Radio
					{...props}
					size={size}
					disabled={disabled}
					required={required}
					name={name}
					value={value}
					checked={checked}
					defaultChecked={defaultChecked}
				/>
			</span>
			{(description || trailingDescription) && (
				<span className={css.body}>
					{description && (
						<span className={css.description}>{description}</span>
					)}
					{trailingDescription && (
						<span className={css.trailingDescription}>
							{trailingDescription}
						</span>
					)}
				</span>
			)}
		</label>
	);
}
